import { pdf } from '@react-pdf/renderer';
import { Download, Printer, ArrowLeft } from 'lucide-react';
import { useMemo } from 'react';
import { Navigate, Link } from 'react-router-dom';

import { CertificateDisplay } from './components/certificate-display';
import { CertificatePdf } from './components/certificate-pdf';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/use-toast';
import { useTrainingProgress } from '@/hooks/use-training-progress';
import { useAuthStore } from '@/stores/auth-store';

export function CertificatePage() {
  const user = useAuthStore((s) => s.user);
  const { data: progress, isLoading } = useTrainingProgress();

  const allDone = progress != null && progress.total_modules > 0 && progress.completed_modules === progress.total_modules;
  const managerName = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || 'Manager';
  const orgName = user?.organization?.name ?? 'Organization';

  const completionDate = useMemo(() => {
    if (!progress?.modules) return new Date().toISOString();
    const completedDates = progress.modules
      .filter((m) => m.completed_at)
      .map((m) => new Date(m.completed_at!).getTime());
    return completedDates.length > 0
      ? new Date(Math.max(...completedDates)).toISOString()
      : new Date().toISOString();
  }, [progress]);

  const certificateId = useMemo(() => {
    if (!user?.id) return 'MW-000000';
    return `MW-${user.id.slice(0, 8).toUpperCase()}`;
  }, [user]);

  if (isLoading) {
    return (
      <div className="mx-auto max-w-3xl space-y-6">
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!allDone) {
    return <Navigate to="/progress" replace />;
  }

  const handleDownloadPdf = async () => {
    try {
      const blob = await pdf(
        <CertificatePdf
          managerName={managerName}
          organizationName={orgName}
          completionDate={completionDate}
          certificateId={certificateId}
        />,
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Metaonia-Certificate-${managerName.replace(/\s+/g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast({ title: 'PDF download failed', description: 'Please try again or use the Print option instead.', variant: 'destructive' });
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/progress">
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <h1 className="text-2xl font-bold">Your Certificate</h1>
      </div>

      <CertificateDisplay
        managerName={managerName}
        organizationName={orgName}
        completionDate={completionDate}
        certificateId={certificateId}
      />

      <div className="flex justify-center gap-3">
        <Button onClick={handleDownloadPdf}>
          <Download className="mr-2 h-4 w-4" />
          Download PDF
        </Button>
        <Button variant="outline" onClick={() => window.print()}>
          <Printer className="mr-2 h-4 w-4" />
          Print
        </Button>
      </div>
    </div>
  );
}
